import { useEffect } from 'react';
import * as profiling from '@ardoq/profiling';
import { Team, TransactionOptions } from '@ardoq/profiling';
import { ViewIds } from '@ardoq/api-types';
import { dispatchAction } from '@ardoq/rxbeach';
import { notifyViewRenderDone } from 'tabview/actions';
import type { ArdoqGraphComponentViewModel, GraphItemsModel } from './types';
import { Extend } from '@ardoq/common-helpers';

export type UsePerformanceTrackingOptions = Extend<
  TransactionOptions,
  {
    team: Team;
    viewId: ViewIds;
  }
>;

export const usePerformanceTracking = (
  name: string,
  threshold: number,
  options: UsePerformanceTrackingOptions,
  viewIsReady: boolean = true
): void => {
  const { team, ...transactionOptions } = options;
  const { viewId } = options;
  profiling.useTransaction(name, threshold, transactionOptions, team);

  useEffect(() => {
    if (viewIsReady) {
      dispatchAction(notifyViewRenderDone(viewId));
    }
  }, [viewIsReady, viewId]);
};

const metadataFromViewModel = (viewModel: ArdoqGraphComponentViewModel) => ({
  ...metadataFromGraphItemsModel(viewModel.nodes, 'nodes'),
  ...metadataFromGraphItemsModel(viewModel.edges, 'edges'),
  ...metadataFromGraphItemsModel(viewModel.groups, 'groups'),
});

const metadataFromGraphItemsModel = (
  itemsModel: GraphItemsModel,
  prefix: string
) => ({
  [`${prefix}Added`]: itemsModel.add.length,
  [`${prefix}Updated`]: itemsModel.update.length,
  [`${prefix}Removed`]: itemsModel.remove.length,
});

export const useGraphViewPerformanceTracking = (
  viewModel: ArdoqGraphComponentViewModel,
  viewId: ViewIds
): void => {
  usePerformanceTracking('graph view render', 1000, {
    viewId,
    team: Team.INSIGHT,
    metadata: metadataFromViewModel(viewModel),
  });
};
